import React, {useEffect, useState} from 'react'
import {QUESTION_TYPE} from '../../utils/constants'
import PercentageQuestionRender from '../../components/QuestionTypeComponents/PercentageQuestionRender'
import NumberSliderRender from '../../components/QuestionTypeComponents/NumberSliderRender'
import TextQuestionRender from '../../components/QuestionTypeComponents/TextQuestionRender'
import MultipleOptionRender from '../../components/QuestionTypeComponents/MultipleOptionRender'
import WbButton from '../../components/common/WbButton'
import WbOutlineButton from '../../components/common/WbOutlineButton'
import MultipleSelectRender from '../../components/QuestionTypeComponents/MultipleSelectRender'
import WbAlert from '../../components/common/WbAlert'
import surveysApi from '../../services/api/surveys/surveysApi'
import {Button, Grid, Stack} from '@mui/material'
import {useParams} from 'react-router-dom'
import {styled} from '@mui/material/styles'
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress'
import YesNoMayRender from '../../components/QuestionTypeComponents/YesNoMayRender'

const BorderLinearProgress = styled(LinearProgress)(({theme}) => ({
  height: 10,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    // Uses the survey's accent colour inside a themed survey (WI-409); falls back
    // to the original default everywhere else.
    backgroundColor: 'var(--survey-accent, #007A7A)',
    ...theme.applyStyles('dark', {
      backgroundColor: 'var(--survey-accent, #007A7A)',
    }),
  },
}))

export default function Questions({
  tab,
  questions,
  setSurveyEnd,
  participantToken,
  handleCompleteSurvey,
}) {
  const {token} = useParams()
  const [sel, setSel] = useState(0)
  const [responses, setResponses] = useState([])
  const [blockSubmit, setBlockSubmit] = useState(false)

  const updateAnswers = value => {
    let resp = responses
    let idx = resp?.findIndex(r => r.questionId === value.questionId)
    if (idx === -1) {
      resp.push(value)
      idx = resp.length - 1
    } else {
      resp[idx] = value
    }
    setResponses(resp)
    if (
      idx === questions?.length - 1 ||
      questions[idx].questionType === QUESTION_TYPE.MULTIPLE_SELECT ||
      questions[idx].questionType === QUESTION_TYPE.NUMBER_SLIDER ||
      questions[idx].questionType === QUESTION_TYPE.PERCENTAGE ||
      questions[idx].questionType === QUESTION_TYPE.COMMENTS ||
      questions[idx].hasFollowup
    ) {
      return
    }
    gotoNextQuestion()
  }

  const submitResponses = () => {
    setBlockSubmit(true)
    surveysApi
      .submit({
        participantToken: participantToken ?? token,
        answers: responses,
        audience: tab,
      })
      .then(() => {
        if (handleCompleteSurvey) {
          handleCompleteSurvey(responses)
        } else {
          setSurveyEnd(true)
        }
      })
      .catch(err =>
        WbAlert({message: 'Error submitting response' + err, type: 'error'})
      )
      .finally(() => setBlockSubmit(false))
  }

  const gotoNextQuestion = () => {
    if (questions[sel].questionType === QUESTION_TYPE.MULTIPLE_SELECT) {
      let settings = questions[sel].questionSettings
      let operator = settings.multiSelectOperator
      let val = settings.multiSelectValue
      let res = responses?.find(x => x.questionId === questions[sel].id)

      let selectedCount = 0
      for (let i = 1; i <= 10; i++) {
        if (res[`value${i}`] !== null) {
          selectedCount++
        }
      }
      if (operator == 1 && selectedCount != val) {
        WbAlert({
          message: `Please select exactly ${val} options`,
          type: 'error',
        })
        return
      }

      if (operator == 2 && selectedCount < val) {
        WbAlert({
          message: `Please select at least ${val} options`,
          type: 'error',
        })
        return
      }

      if (operator == 3 && selectedCount > val) {
        WbAlert({
          message: `Please select at most ${val} options`,
          type: 'error',
        })
        return
      }
    }

    responses?.findIndex(x => x.questionId === questions[sel].id) > -1
      ? setSel(sel + 1)
      : WbAlert({message: 'Please answer the question', type: 'error'})
  }

  const getPreview = (templateQuestion, i) => {
    const question = {
      ...templateQuestion,
      questionSettings: {...templateQuestion.questionSettings},
      followupQuestions:
        templateQuestion.followupQuestions?.length > 0
          ? [...templateQuestion.followupQuestions]
          : null,
      multiOptionResponseAttributes:
        templateQuestion.multiOptionResponseAttributes?.length > 0
          ? [...templateQuestion.multiOptionResponseAttributes]
          : null,
      sliderResponseAttributes: {...templateQuestion.sliderResponseAttributes},
      openResponseAttributes: {...templateQuestion.openResponseAttributes},
    }
    switch (question.questionType) {
      case QUESTION_TYPE.MULTIPLE_SELECT:
        return (
          <MultipleSelectRender
            question={{...question}}
            audience={tab}
            updateAnswers={updateAnswers}
          />
        )

      case QUESTION_TYPE.YES_NO:
      case QUESTION_TYPE.YES_NO_MAYBE:
        ;<YesNoMayRender
          question={{...question}}
          audience={tab}
          updateAnswers={updateAnswers}
        />
      case QUESTION_TYPE.LIKERT:
      case QUESTION_TYPE.MULTIPLE_CHOICE:
        return (
          <MultipleOptionRender
            question={{...question}}
            audience={tab}
            updateAnswers={updateAnswers}
          />
        )
      case QUESTION_TYPE.COMMENTS:
        return (
          <TextQuestionRender
            question={{...question}}
            audience={tab}
            updateAnswers={updateAnswers}
          />
        )
      case QUESTION_TYPE.NUMBER_SLIDER:
        return (
          <NumberSliderRender
            question={{...question}}
            audience={tab}
            updateAnswers={updateAnswers}
          />
        )
      case QUESTION_TYPE.PERCENTAGE:
        return (
          <PercentageQuestionRender
            question={{...question}}
            audience={tab}
            updateAnswers={updateAnswers}
          />
        )
      default:
        return <div>Question type not available {question.questionType}</div>
    }
  }

  return (
    <div className="survey-preview">
      <div className="question-preview grow">
        <Grid container justifyContent="center" textAlign="center">
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <div className="preview">
              {questions?.map((q, i) => (
                <div
                  className="question-slider"
                  style={{
                    transition: 'all 0.5s',
                    visibility: i === sel ? 'visible' : 'hidden',
                    opacity: i === sel ? 1 : 0,
                    transform:
                      i < sel
                        ? `translateX(${i === sel ? 0 : -100}%)`
                        : `translateX(${i === sel ? 0 : 100}%)`,
                  }}
                >
                  <div className="question-box">{getPreview(q, i)}</div>
                </div>
              ))}
            </div>
            <div className="bottom-nav">
              <div className="question-nav">
                <Grid container>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Stack direction="row" justifyContent="space-between">
                      <div>
                        {sel === 0 ? (
                          ''
                        ) : (
                          <WbOutlineButton
                            onClick={() => setSel(sel - 1)}
                            CustomOutlineButtonText={'Previous'}
                            disabled={blockSubmit}
                          />
                        )}
                      </div>
                      <div>
                        {sel >= questions?.length - 1 ? (
                          <WbButton
                            onClick={submitResponses}
                            CustomButtonText={'Submit'}
                            disabled={blockSubmit}
                            loading={blockSubmit}
                          />
                        ) : (
                          <WbButton
                            onClick={gotoNextQuestion}
                            CustomButtonText={'Next'}
                          />
                        )}
                      </div>
                    </Stack>
                  </Grid>
                </Grid>
              </div>
              <BorderLinearProgress
                variant="determinate"
                value={(sel * 100) / questions?.length}
              />
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}
